import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import './Pages.css';

const API_BASE = 'https://main.victriedemos.com:3017';

const logoUrl = (logo) => {
  if (!logo) return '';
  return logo.startsWith('http') ? logo : `${API_BASE}${logo}`;
};

const CreatePolicy = ({ setActiveMenu }) => {
  // State for steps
  const [currentStep, setCurrentStep] = useState(1);
  
  // State for Step 1: Quote
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [insuredItems, setInsuredItems] = useState([]);
  const [selectedInsuredItem, setSelectedInsuredItem] = useState(null);
  const [vehicleValue, setVehicleValue] = useState('');
  const [capacity, setCapacity] = useState('');
  const [tonnage, setTonnage] = useState('');
  const [coverPeriod, setCoverPeriod] = useState('Annually');
  const [quotes, setQuotes] = useState(null);
  const [selectedUnderwriter, setSelectedUnderwriter] = useState(null);
  const [quoteBreakdown, setQuoteBreakdown] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  
  // State for Step 2: Client
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [clientData, setClientData] = useState({
    FirstName: '',
    LastName: '',
    Surname: '',
    Email: '',
    Phone: '',
    KraPin: '',
    IDNumber: ''
  });
  const [syncingClient, setSyncingClient] = useState(false);
  const [definiteClientOID, setDefiniteClientOID] = useState(null);

  // State for Step 3: Vehicle
  const [vehicleData, setVehicleData] = useState({
    Registration: '',
    Make: '',
    Model: '',
    Chassis: '',
    Engine: '',
    YOM: '',
    CC: '',
    Color: '',
    CommencementDate: '',
    ExpiryDate: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [showMakeSuggestions, setShowMakeSuggestions] = useState(false);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/definite/makes`);
        const data = await response.json();
        if (data.success) {
          setVehicleMakes(data.makes);
        }
      } catch (err) {
        console.error('Error fetching vehicle makes:', err);
      }
    };
    fetchVehicleMakes();
  }, []);

  // Load products on mount
  useEffect(() => {
    fetchProducts();
    fetchClients();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products');
      const data = await response.json();
      if (data.success) {
        setProducts(data.products.filter(p => p.Status === 1));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      alert('Failed to load products');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/clients');
      const data = await response.json();
      if (data.success) {
        setClients(data.clients);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleProductChange = async (productId) => {
    const product = products.find(p => p.Id === parseInt(productId));
    setSelectedProduct(product);
    setSelectedInsuredItem(null);
    // TOR products (PrdCode 4713/4714) are only ever written monthly
    setCoverPeriod(product && (product.PrdCode === 4713 || product.PrdCode === 4714) ? 'Monthly' : 'Annually');
    setQuotes(null);
    setSelectedUnderwriter(null);
    setQuoteBreakdown(null);

    if (productId) {
      try {
        const response = await fetch(`https://main.victriedemos.com:3017/api/products/${productId}`);
        const data = await response.json();
        if (data.success) {
          setInsuredItems(data.insuredItems);
          // TOR products only ever have one cover type - pick it automatically
          const isTorProduct = product && (product.PrdCode === 4713 || product.PrdCode === 4714);
          if (isTorProduct && data.insuredItems.length === 1) {
            setSelectedInsuredItem(data.insuredItems[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching insured items:', err);
      }
    } else {
      setInsuredItems([]);
    }
  };

  const getQuotes = async () => {
    if (!selectedProduct || !selectedInsuredItem) {
      alert('Please select product and insured item');
      return;
    }

    // Check if vehicle value is required (for CalculationType 1)
    if (selectedInsuredItem.CalculationType === 1 && !vehicleValue) {
      alert('Vehicle value is required for this cover type');
      return;
    }

    // Check if capacity is required (for CalculationType 3)
    if (selectedInsuredItem.CalculationType === 3 && !capacity) {
      alert('Seating capacity is required for this cover type');
      return;
    }

    setLoadingQuote(true);
    setQuotes(null);
    setSelectedUnderwriter(null);
    setQuoteBreakdown(null);

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/get-quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insuredItemId: selectedInsuredItem.Id,
          productId: selectedProduct.Id,
          vehicleValue: vehicleValue ? parseFloat(vehicleValue) : 0,
          capacity: capacity ? parseInt(capacity) : null,
          tonnage: tonnage ? parseFloat(tonnage) : 0,
          coverPeriod
        })
      });

      const data = await response.json();

      if (data.success) {
        setQuotes(data.quotes);
      } else {
        alert(data.message || 'Failed to get quotes');
      }
    } catch (err) {
      console.error('Error getting quotes:', err);
      alert('Failed to get quotes');
    } finally {
      setLoadingQuote(false);
    }
  };

  const selectUnderwriter = (companyId) => {
    const quote = quotes?.find(q => q.companyId === companyId);
    if (!quote || quote.error) return;
    setSelectedUnderwriter(companyId);
    setQuoteBreakdown(quote);
  };

  const downloadQuote = () => {
    if (!quoteBreakdown) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZAKIM INSURANCE AGENCY', 105, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Insurance Quote', 105, 23, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    let y = 45;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Quote Details', 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const details = [
      ['Product:', selectedProduct.ProductName],
      ['Cover Type:', selectedInsuredItem.InsuredItemName],
      ['Underwriter:', quoteBreakdown?.name || 'N/A'],
      ['Cover Duration:', coverPeriod],
      ['Vehicle Value:', formatCurrency(vehicleValue)],
      ['Capacity:', capacity || 'N/A'],
      ['Date:', new Date().toLocaleDateString()]
    ];

    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 20, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 70, y);
      y += 7;
    });

    y += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Premium Breakdown', 20, y);
    y += 10;
    
    doc.setFontSize(10);
    const breakdown = [
      ['Basic Premium:', formatCurrency(quoteBreakdown.basicPremium)],
      ['PHCF (0.25%):', formatCurrency(quoteBreakdown.phcf)],
      ['Training Levy (0.2%):', formatCurrency(quoteBreakdown.trainingLevy)],
      ['Stamp Duty:', formatCurrency(quoteBreakdown.stampDuty)],
      ['', ''],
      ['Gross Premium:', formatCurrency(quoteBreakdown.grossPremium)]
    ];

    breakdown.forEach(([label, value]) => {
      doc.setFont('helvetica', label === 'Gross Premium:' ? 'bold' : 'normal');
      doc.text(label, 20, y);
      doc.text(value, 70, y);
      y += 7;
    });

    doc.save(`Quote_${selectedProduct.ProductName}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const handleClientChange = (clientEmail) => {
    const client = clients.find(c => c.Email === clientEmail);
    if (client) {
      setSelectedClient(client);
      setClientData({
        FirstName: client.FirstName || '',
        LastName: client.LastName || '',
        Surname: client.Surname || '',
        Email: client.Email || '',
        Phone: client.Phone || '',
        KraPin: client.KraPin || '',
        IDNumber: client.IDNumber || ''
      });
      setShowNewClientForm(false);
    } else {
      setSelectedClient(null);
    }
  };

  const handleNewClient = () => {
    setShowNewClientForm(true);
    setSelectedClient(null);
    setClientData({
      FirstName: '',
      LastName: '',
      Surname: '',
      Email: '',
      Phone: '',
      KraPin: '',
      IDNumber: ''
    });
  };

  const handleClientDataChange = (e) => {
    setClientData({
      ...clientData,
      [e.target.name]: e.target.value
    });
  };

  const handleVehicleDataChange = (e) => {
    const { name, value } = e.target;
    setVehicleData({
      ...vehicleData,
      [name]: value
    });

    // Auto-calculate expiry date when commencement date changes
    if (name === 'CommencementDate' && value) {
      const commencementDate = new Date(value);
      const expiryDate = new Date(commencementDate);
      if (coverPeriod === '2installments' || coverPeriod === 'Monthly') {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      } else if (coverPeriod === 'Week') {
        expiryDate.setDate(expiryDate.getDate() + 7);
      } else if (coverPeriod === 'Fortnight') {
        expiryDate.setDate(expiryDate.getDate() + 14);
      } else {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      }
      expiryDate.setDate(expiryDate.getDate() - 1);

      setVehicleData(prev => ({
        ...prev,
        ExpiryDate: expiryDate.toISOString().split('T')[0]
      }));
    }
  };

  const handleSubmitPolicy = async () => {
    // Validation
    if (!clientData.Email || !clientData.FirstName || !clientData.LastName) {
      alert('Please fill in all required client fields');
      return;
    }

    if (!vehicleData.Registration || !vehicleData.Make || !vehicleData.Model || 
        !vehicleData.CommencementDate || !vehicleData.ExpiryDate) {
      alert('Please fill in all required vehicle fields');
      return;
    }

    setSubmitting(true);

    try {
      const userEmail = localStorage.getItem('userEmail');
      
      const response = await fetch('https://main.victriedemos.com:3017/api/create-policy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: clientData,
          vehicle: {
            ...vehicleData,
            SumInsured: parseFloat(vehicleValue),
            Capacity: capacity ? parseInt(capacity) : null,
            Tonnage: tonnage ? parseFloat(tonnage) : 0
          },
          policy: {
            Product: selectedProduct.Id,
            InsuredItem: selectedInsuredItem.Id,
            CoverType: selectedInsuredItem.InsuredItemName,
            Underwriter: selectedUnderwriter,
            CoverPeriod: coverPeriod,
            GrossPremium: quoteBreakdown.grossPremium,
            Commission: quoteBreakdown.grossPremium * 0.10,
            Outstanding: 0,
            CommencementDate: vehicleData.CommencementDate,
            ExpiryDate: vehicleData.ExpiryDate,
            Status: 2, // Pending approval
            DoneBy: userEmail
          },
          isNewClient: showNewClientForm || !selectedClient,
          definiteClientOID
        })
      });

      const data = await response.json();

      if (data.success) {
        let message = '✅ Policy created successfully! Awaiting approval.\nPolicy Number: ' + data.policyNumber;
        if (data.definiteProposal && data.definiteProposal.success === false) {
          message += '\n\n⚠️ Definite Assurance proposal was not submitted: ' + data.definiteProposal.message;
        }

        if (attachedFiles.length > 0) {
          const proposalKey = (data.definiteProposal && data.definiteProposal.success && data.definiteProposal.OID)
            ? data.definiteProposal.OID
            : `POLICY-${data.policyId}`;

          const formData = new FormData();
          attachedFiles.forEach(file => formData.append('files', file));

          try {
            const uploadResponse = await fetch(`https://main.victriedemos.com:3017/api/proposals/${proposalKey}/documents`, {
              method: 'POST',
              body: formData
            });
            const uploadData = await uploadResponse.json();
            if (!uploadData.success) {
              message += '\n\n⚠️ Documents were not uploaded: ' + uploadData.message;
            }
          } catch (uploadErr) {
            console.error('Error uploading proposal documents:', uploadErr);
            message += '\n\n⚠️ Documents were not uploaded due to a network error.';
          }
        }

        alert(message);
        // Reset and go back to policies
        if (setActiveMenu) {
          setActiveMenu('new-policies');
        }
      } else {
        alert('❌ Failed to create policy: ' + data.message);
      }
    } catch (err) {
      console.error('Error creating policy:', err);
      alert('❌ An error occurred while creating policy');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !quoteBreakdown) {
      alert('Please get a quote and select an underwriter before proceeding');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleClientNext = async () => {
    if (!clientData.Email) {
      alert('Please select or create a client');
      return;
    }
    if (!clientData.IDNumber) {
      alert('ID Number is required');
      return;
    }

    setSyncingClient(true);

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/definite/sync-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: clientData.FirstName,
          lastName: clientData.Surname,
          otherNames: clientData.LastName,
          idNumber: clientData.IDNumber,
          pin: clientData.KraPin,
          phone: clientData.Phone
        })
      });

      const data = await response.json();

      if (data.success) {
        setDefiniteClientOID(data.OID || null);
        setCurrentStep(3);
      } else {
        alert(data.message || 'Failed to verify client with Definite Assurance');
      }
    } catch (err) {
      console.error('Error syncing client with Definite Assurance:', err);
      alert('Unable to verify client with Definite Assurance');
    } finally {
      setSyncingClient(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All data will be lost.')) {
      if (setActiveMenu) {
        setActiveMenu('new-policies');
      }
    }
  };

  const isCommercialProduct = /commercial/i.test(selectedProduct?.ProductName || '');
  const isMatatuProduct = /matatu/i.test(selectedProduct?.ProductName || '');
  const isTorMonthlyOnlyProduct = selectedProduct?.PrdCode === 4713 || selectedProduct?.PrdCode === 4714;

  const normalizedMake = vehicleData.Make.trim().toLowerCase();
  const makeSuggestions = normalizedMake
    ? vehicleMakes.filter(entry => entry.make.Make.toLowerCase().includes(normalizedMake)).slice(0, 8)
    : [];

  const selectedMakeEntry = vehicleMakes.find(entry => entry.make.Make.toLowerCase() === normalizedMake);
  const normalizedModel = vehicleData.Model.trim().toLowerCase();
  const modelSuggestions = selectedMakeEntry
    ? selectedMakeEntry.models
        .filter(m => !normalizedModel || m.Model.toLowerCase().includes(normalizedModel))
        .slice(0, 8)
    : [];

  const selectVehicleField = (name, value) => {
    handleVehicleDataChange({ target: { name, value } });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>📋 Create New Policy</h1>
          <p className="page-subtitle">Multi-step policy creation wizard</p>
        </div>
        <button className="back-to-policies-btn" onClick={handleCancel}>
          ← Cancel
        </button>
      </div>

      {/* Progress Steps */}
      <div className="steps-container">
        <div className={`step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">{currentStep > 1 ? '✓' : '1'}</div>
          <div className="step-label">💰 Get Quote</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${currentStep === 2 ? 'active' : currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">{currentStep > 2 ? '✓' : '2'}</div>
          <div className="step-label">👤 Select Client</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${currentStep === 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">🚗 Vehicle Details</div>
        </div>
      </div>

      <div className="form-container-narrow">
        {/* STEP 1: GET QUOTE */}
        {currentStep === 1 && (
          <div className="step-content">
            <h2>💰 Step 1: Get Quote</h2>
            
            <div className="form-grid">
              <div className="form-group-icon">
                <label>📦 Select Product *</label>
                <select 
                  value={selectedProduct?.Id || ''} 
                  onChange={(e) => handleProductChange(e.target.value)}
                  className="form-input-narrow"
                >
                  <option value="">-- Select Product --</option>
                  {products.map(product => (
                    <option key={product.Id} value={product.Id}>
                      {product.ProductName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-icon">
                <label>🛡️ Select Cover Type *</label>
                <select 
                  value={selectedInsuredItem?.Id || ''} 
                  onChange={(e) => {
                    const item = insuredItems.find(i => i.Id === parseInt(e.target.value));
                    setSelectedInsuredItem(item);
                    setQuoteBreakdown(null);
                  }}
                  className="form-input-narrow"
                  disabled={!selectedProduct || isTorMonthlyOnlyProduct}
                >
                  <option value="">-- Select Cover Type --</option>
                  {insuredItems.map(item => (
                    <option key={item.Id} value={item.Id}>
                      {item.InsuredItemName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group-icon">
                <label>💵 Vehicle Value (KES) {selectedInsuredItem?.CalculationType === 1 ? '*' : ''}</label>
                <input
                  type="number"
                  value={vehicleValue}
                  onChange={(e) => {
                    setVehicleValue(e.target.value);
                    setQuoteBreakdown(null);
                  }}
                  className="form-input-narrow"
                  placeholder="Enter vehicle value"
                  disabled={selectedInsuredItem?.CalculationType === 2}
                />
              </div>

              <div className="form-group-icon">
                <label>💺 Seating Capacity {selectedInsuredItem?.CalculationType === 3 ? '*' : ''}</label>
                <input
                  type="number"
                  value={capacity}
                  onChange={(e) => {
                    setCapacity(e.target.value);
                    setQuoteBreakdown(null);
                  }}
                  className="form-input-narrow"
                  placeholder="Enter seating capacity"
                />
              </div>
            </div>

            <div className="form-grid">
              {isCommercialProduct && (
                <div className="form-group-icon">
                  <label>🚛 Tonnage</label>
                  <input
                    type="number"
                    value={tonnage}
                    max={selectedProduct?.PrdCode === 4714 ? 3 : undefined}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (selectedProduct?.PrdCode === 4714 && value !== '' && parseFloat(value) > 3) {
                        value = '3';
                      }
                      setTonnage(value);
                      setQuotes(null);
                      setSelectedUnderwriter(null);
                      setQuoteBreakdown(null);
                    }}
                    className="form-input-narrow"
                    placeholder="Enter vehicle tonnage"
                  />
                  {selectedProduct?.PrdCode === 4714 && (
                    <small className="form-hint">Maximum 3 tons for this product</small>
                  )}
                </div>
              )}

              <div className="form-group-icon">
                <label>📆 Cover Duration *</label>
                <select
                  value={coverPeriod}
                  onChange={(e) => {
                    setCoverPeriod(e.target.value);
                    setQuotes(null);
                    setSelectedUnderwriter(null);
                    setQuoteBreakdown(null);
                  }}
                  className="form-input-narrow"
                  disabled={isTorMonthlyOnlyProduct}
                >
                  <option value="Annually">Annually</option>
                  {isMatatuProduct ? (
                    <>
                      <option value="Monthly">Monthly</option>
                      <option value="Week">Week</option>
                      <option value="Fortnight">Fortnight</option>
                    </>
                  ) : isTorMonthlyOnlyProduct ? (
                    <option value="Monthly">Monthly</option>
                  ) : (
                    <option value="2installments">Monthly</option>
                  )}
                </select>
              </div>
            </div>

            <div className="form-actions-center">
              <button
                type="button"
                className="primary-btn-narrow"
                onClick={getQuotes}
                disabled={loadingQuote || !selectedProduct || !selectedInsuredItem}
              >
                {loadingQuote ? '⏳ Getting Quotes...' : '💰 Get Quote'}
              </button>
            </div>

            {quotes && (
              <div className="quote-breakdown">
                <h3>Compare Underwriters</h3>
                {quotes.length === 0 ? (
                  <p className="no-data">No underwriting companies configured. Add one under Underwriting Companies.</p>
                ) : (
                  <div className="underwriter-grid">
                    {quotes.map(q => {
                      const hasError = !!q.error;
                      const isDefinite = /definite/i.test(q.name || '');
                      const isDisabled = hasError || !isDefinite;
                      const isSelected = selectedUnderwriter === q.companyId;
                      return (
                        <div
                          key={q.companyId}
                          className={`underwriter-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                          onClick={() => !isDisabled && selectUnderwriter(q.companyId)}
                        >
                          <img src={logoUrl(q.logo)} alt={q.name} className="underwriter-logo" />
                          <div className="underwriter-name">{q.name}</div>
                          {hasError ? (
                            <div className="underwriter-error">Quote unavailable</div>
                          ) : !isDefinite ? (
                            <>
                              <div className="underwriter-premium">{formatCurrency(q.grossPremium)}</div>
                              <div className="underwriter-error">Currently unavailable</div>
                            </>
                          ) : (
                            <div className="underwriter-premium">{formatCurrency(q.grossPremium)}</div>
                          )}
                          {isSelected && <div className="underwriter-selected-badge">✓ Selected</div>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {quoteBreakdown && (
                  <>
                    <div className="breakdown-table">
                      <div className="breakdown-row">
                        <span>Basic Premium:</span>
                        <strong>{formatCurrency(quoteBreakdown.basicPremium)}</strong>
                      </div>
                      <div className="breakdown-row">
                        <span>PHCF (0.25%):</span>
                        <span>{formatCurrency(quoteBreakdown.phcf)}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>Training Levy (0.2%):</span>
                        <span>{formatCurrency(quoteBreakdown.trainingLevy)}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>Stamp Duty:</span>
                        <span>{formatCurrency(quoteBreakdown.stampDuty)}</span>
                      </div>
                      <div className="breakdown-row total">
                        <span><strong>Gross Premium:</strong></span>
                        <strong className="gross-premium">{formatCurrency(quoteBreakdown.grossPremium)}</strong>
                      </div>
                    </div>

                    <div className="quote-actions">
                      <button type="button" className="action-button-narrow" onClick={downloadQuote}>
                        📄 Download Quote
                      </button>
                      <button type="button" className="primary-btn-narrow" onClick={nextStep}>
                        🛒 Buy Cover →
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: SELECT CLIENT */}
        {currentStep === 2 && (
          <div className="step-content">
            <h2>👤 Step 2: Select Client</h2>
            
            <div className="client-selection-narrow">
              <div className="form-group-icon">
                <label>📧 Select Existing Client</label>
                <select 
                  value={selectedClient?.Email || ''} 
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="form-input-narrow"
                  disabled={showNewClientForm}
                >
                  <option value="">-- Select Client --</option>
                  {clients.map(client => (
                    <option key={client.Email} value={client.Email}>
                      {client.FirstName} {client.LastName} - {client.Email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="or-divider">
                <span>OR</span>
              </div>

              <button 
                type="button" 
                className="secondary-btn-narrow"
                onClick={handleNewClient}
              >
                ➕ Create New Client
              </button>
            </div>

            {(selectedClient || showNewClientForm) && (
              <div className="client-form">
                <h3>{showNewClientForm ? '➕ New Client Details' : '📋 Client Details'}</h3>
                
                <div className="form-grid">
                  <div className="form-group-icon">
                    <label>👤 First Name *</label>
                    <input
                      type="text"
                      name="FirstName"
                      value={clientData.FirstName}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>

                  <div className="form-group-icon">
                    <label>👤 Last Name *</label>
                    <input
                      type="text"
                      name="LastName"
                      value={clientData.LastName}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group-icon">
                    <label>👥 Surname</label>
                    <input
                      type="text"
                      name="Surname"
                      value={clientData.Surname}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>

                  <div className="form-group-icon">
                    <label>📧 Email *</label>
                    <input
                      type="email"
                      name="Email"
                      value={clientData.Email}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group-icon">
                    <label>📱 Phone *</label>
                    <input
                      type="tel"
                      name="Phone"
                      value={clientData.Phone}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>

                  <div className="form-group-icon">
                    <label>🆔 ID Number *</label>
                    <input
                      type="text"
                      name="IDNumber"
                      value={clientData.IDNumber}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group-icon">
                    <label>🏦 KRA PIN</label>
                    <input
                      type="text"
                      name="KraPin"
                      value={clientData.KraPin}
                      onChange={handleClientDataChange}
                      className="form-input-narrow"
                      readOnly={!showNewClientForm}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions-center">
              <button type="button" className="secondary-btn-narrow" onClick={prevStep}>
                ← Back
              </button>
              <button
                type="button"
                className="primary-btn-narrow"
                onClick={handleClientNext}
                disabled={!clientData.Email || syncingClient}
              >
                {syncingClient ? '⏳ Verifying Client...' : 'Next →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: VEHICLE DETAILS */}
        {currentStep === 3 && (
          <div className="step-content">
            <h2>🚗 Step 3: Vehicle Details</h2>
            
            <div className="form-grid">
              <div className="form-group-icon">
                <label>🚙 Registration Number *</label>
                <input
                  type="text"
                  name="Registration"
                  value={vehicleData.Registration}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="e.g., KAA 123A"
                />
              </div>

              <div className="form-group-icon autocomplete-field">
                <label>🏭 Make *</label>
                <input
                  type="text"
                  name="Make"
                  value={vehicleData.Make}
                  onChange={handleVehicleDataChange}
                  onFocus={() => setShowMakeSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowMakeSuggestions(false), 150)}
                  className="form-input-narrow"
                  placeholder="Start typing to search, e.g., Toyota"
                  autoComplete="off"
                />
                {showMakeSuggestions && makeSuggestions.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {makeSuggestions.map((entry) => (
                      <li
                        key={entry.make.MakeOID}
                        onMouseDown={() => {
                          selectVehicleField('Make', entry.make.Make);
                          setShowMakeSuggestions(false);
                        }}
                      >
                        {entry.make.Make}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group-icon autocomplete-field">
                <label>🚗 Model *</label>
                <input
                  type="text"
                  name="Model"
                  value={vehicleData.Model}
                  onChange={handleVehicleDataChange}
                  onFocus={() => setShowModelSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowModelSuggestions(false), 150)}
                  className="form-input-narrow"
                  placeholder={selectedMakeEntry ? 'Start typing to search' : 'Select a make first'}
                  autoComplete="off"
                />
                {showModelSuggestions && modelSuggestions.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {modelSuggestions.map((m) => (
                      <li
                        key={m.ModelOID}
                        onMouseDown={() => {
                          selectVehicleField('Model', m.Model.trim());
                          setShowModelSuggestions(false);
                        }}
                      >
                        {m.Model}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group-icon">
                <label>📅 Year of Manufacture *</label>
                <input
                  type="number"
                  name="YOM"
                  value={vehicleData.YOM}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="e.g., 2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group-icon">
                <label>🔧 Chassis Number *</label>
                <input
                  type="text"
                  name="Chassis"
                  value={vehicleData.Chassis}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="Enter chassis number"
                />
              </div>

              <div className="form-group-icon">
                <label>⚙️ Engine Number *</label>
                <input
                  type="text"
                  name="Engine"
                  value={vehicleData.Engine}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="Enter engine number"
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group-icon">
                <label>🔢 CC</label>
                <input
                  type="number"
                  name="CC"
                  value={vehicleData.CC}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="e.g., 1500"
                />
              </div>

              <div className="form-group-icon">
                <label>🎨 Color *</label>
                <input
                  type="text"
                  name="Color"
                  value={vehicleData.Color}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  placeholder="e.g., White"
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group-icon">
                <label>📆 Commencement Date *</label>
                <input
                  type="date"
                  name="CommencementDate"
                  value={vehicleData.CommencementDate}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                />
              </div>

              <div className="form-group-icon">
                <label>📆 Expiry Date *</label>
                <input
                  type="date"
                  name="ExpiryDate"
                  value={vehicleData.ExpiryDate}
                  onChange={handleVehicleDataChange}
                  className="form-input-narrow"
                  readOnly
                />
              </div>
            </div>

            {/* Attach Documents */}
            <div className="form-group-icon" style={{ marginTop: 20 }}>
              <label>📎 Attach Documents (optional)</label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files);
                  e.target.value = '';
                  setAttachedFiles(prev => [...prev, ...newFiles]);
                }}
                className="form-input-narrow"
              />
              {attachedFiles.length > 0 && (
                <ul className="autocomplete-suggestions" style={{ position: 'static', boxShadow: 'none', border: '1px solid #e0e0e0', marginTop: 8 }}>
                  {attachedFiles.map((file, index) => (
                    <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'default' }}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="clear-search-btn"
                        onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                      >
                        ✕ Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Summary Section */}
            <div className="policy-summary">
              <h3>📊 Policy Summary</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <label>📦 Product:</label>
                  <span>{selectedProduct?.ProductName}</span>
                </div>
                <div className="summary-item">
                  <label>🛡️ Cover Type:</label>
                  <span>{selectedInsuredItem?.InsuredItemName}</span>
                </div>
                <div className="summary-item">
                  <label>🏢 Underwriter:</label>
                  <span>{quoteBreakdown?.name}</span>
                </div>
                <div className="summary-item">
                  <label>👤 Client:</label>
                  <span>{clientData.FirstName} {clientData.LastName}</span>
                </div>
                <div className="summary-item">
                  <label>💰 Gross Premium:</label>
                  <strong>{formatCurrency(quoteBreakdown?.grossPremium)}</strong>
                </div>
              </div>
            </div>

            <div className="form-actions-center">
              <button type="button" className="secondary-btn-narrow" onClick={prevStep}>
                ← Back
              </button>
              <button 
                type="button" 
                className="primary-btn-narrow submit-policy-btn" 
                onClick={handleSubmitPolicy}
                disabled={submitting}
              >
                {submitting ? '⏳ Submitting...' : '✓ Submit Policy for Approval'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePolicy;
